/**
 * Sentry initialization — scaffold no-op pattern.
 *
 * Initialized only when REACT_APP_SENTRY_DSN is set so this is safe to ship
 * to production without a key. Once you set the env var (in Emergent prod
 * env config) and redeploy, Sentry starts capturing automatically.
 *
 * Cost-aware defaults:
 *   - traces sample rate 0.1 (10% perf transactions)
 *   - replays disabled (replays are the spendy part)
 *   - chunk-load errors dropped before send (deployment artifact, not a bug)
 *
 * Per playbook iteration_23 (2026-05-06).
 */
import * as Sentry from '@sentry/react';

let _initialized = false;

export function initializeSentry() {
  if (_initialized) return;
  _initialized = true;
  const dsn = (process.env.REACT_APP_SENTRY_DSN || '').trim();
  if (!dsn) {
    // eslint-disable-next-line no-console
    console.info('Sentry monitoring disabled (no REACT_APP_SENTRY_DSN set)');
    return;
  }
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'preview';
  Sentry.init({
    dsn,
    environment: env,
    release: process.env.REACT_APP_RELEASE || undefined,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend(event, hint) {
      // Drop chunk-load errors — they're deployment-update artifacts, not bugs.
      const exc = event?.exception?.values?.[0];
      const msg = (exc?.value || '').toString();
      if (
        msg.includes('ChunkLoadError') ||
        msg.includes('Loading chunk') ||
        msg.includes('Loading CSS chunk') ||
        msg.includes("Failed to fetch dynamically imported module")
      ) {
        return null;
      }
      // Drop ResizeObserver loop errors — universal browser noise.
      if (msg.includes('ResizeObserver loop')) return null;
      return event;
    },
    beforeBreadcrumb(crumb) {
      // Reduce console-log noise in breadcrumbs
      if (crumb.category === 'console' && (crumb.level === 'debug' || crumb.level === 'log')) {
        return null;
      }
      return crumb;
    },
  });
  // eslint-disable-next-line no-console
  console.info(`Sentry initialized (env=${env})`);
}
